import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { session } from '../../../services/session';
import { api } from '../../../services/api';
import { useForm, usePublisherList } from '../../../utils/custom-hooks';

import FormSection from '../../ui/form-elements/form-page/form-section';
import InputWrapper from '../../ui/form-elements/form-page/input-wrapper';
import TargetExpressionInput from './target-expression-input';
import Button from '../../ui/button';
import { Loader } from '../../misc/loader-utils';

const TargetingPage = props => {
  const {
    strategy,
    onSubmit,
    formError,
    merchantId,
    strategyId,
    strategyGroupId,
    handleBackButton,
    setIsDirty,
  } = props;
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [validators, setValidators] = useState();

  const [pubList, setPubList, pubMap] = usePublisherList(session.orgId, true, true);

  const handlePageSubmit = () => {
    if (!isDirty) {
      onSubmit(true);
      return;
    }

    saveTargeting()
      .then(() => {
        onSubmit();
      })
      .catch(err => console.log(err));
  };

  const skuValidator = () => {
    return {
      validatorFn: value => {
        if (
          value.target_list_items.length === 0 ||
          value.target_rule_operator === null ||
          value.target_list_type === null
        ) {
          return false;
        }
        return true;
      },
      errorMessage: 'All fields are required',
    };
  };

  const { values, setValues, errors, handleChange, handleSubmit, isDirty } = useForm(
    handlePageSubmit,
    validators,
  );

  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty]);

  //  Set Validators when removing and adding target_rules
  useEffect(() => {
    const validators = {};
    Object.keys(values).forEach(field => {
      validators[field] = [skuValidator()];
    });
    setValidators(validators);
  }, [Object.entries(values).length]);

  //  Get existing target expression
  useEffect(() => {
    const endpoint = `/api/v0/merchants/${merchantId}/target_expressions/${strategy.target_expression_id}/target_expression_by_id/`; // get target expression endpoint;

    if (strategy.target_expression_id) {
      setLoading(true);
      api.get(endpoint).then(async resp => {
        const targetRules = resp.data.data[0].target_rules;
        formatValues(targetRules).then(targetExpression => {
          setValues(targetExpression);
          setLoading(false);
        });
      });
    }
  }, []);

  //  Incoming data formatting
  const formatValues = async targetRules => {
    const targetValuePromises = await targetRules.map(rule => {
      return getRuleValue(rule);
    });

    return Promise.all(targetValuePromises).then(targetRuleValues => {
      const targetExpressionValues = {};
      targetRuleValues.forEach(rule => {
        if (rule) {
          targetExpressionValues[rule.target_rule_id] = rule;
        }
      });

      return targetExpressionValues;
    });
  };

  const getRuleValue = rule => {
    const typeIdPair = Object.entries(rule).filter(([listType, listId]) => {
      return !['target_rule_operator', 'target_rule_id'].includes(listType) && listId !== null;
    })[0];

    if (!typeIdPair) {
      return null;
    }

    const targetListType = getTargetTypeByField(typeIdPair[0]);
    const targetListId = typeIdPair[1];

    return getTargetListById(targetListType, targetListId).then(targetList => {
      const targetListItems = targetList.map(item => {
        return item[`target_${targetListType}`];
      });

      return {
        target_rule_id: rule.target_rule_id,
        target_list_type: targetListType,
        target_rule_operator: rule.target_rule_operator,
        target_list_items: targetListItems,
        target_list: targetList,
        target_list_id: targetListId,
      };
    });
  };

  const getTargetListById = (listType, listId) => {
    const endpoint = `/api/v0/merchants/${merchantId}/target_lists/${listType}/${listId}/target_list_by_id/`;

    //  Arbitrarily large per_page value to avoid paging for now.
    //  SKU dropdown will need rework before paging is functional
    return api.get(endpoint, { per_page: 50000 }).then(res => {
      return res.data.data[0].target_list;
    });
  };

  const saveTargeting = async () => {
    const targetRulesData = await Promise.all(
      Object.entries(values).map(async ([targetRuleId, listValues]) => {
        const {
          target_list_items,
          target_list_type,
          target_list_id,
          target_rule_operator,
        } = listValues;

        let payloadListItems = target_list_items.slice();

        if (target_list_type === 'publisher') {
          payloadListItems = target_list_items.map(item => parseInt(item));
        }

        const targetListId = target_list_id
          ? await updateTargetList(payloadListItems, target_list_type, target_list_id)
          : await createTargetList(payloadListItems, target_list_type);

        return {
          targetRuleId: targetRuleId.startsWith('new') ? null : targetRuleId,
          targetListId,
          targetOperator: target_rule_operator,
          targetListType: target_list_type,
        };
      }),
    );

    return saveTargetExpression(targetRulesData);
  };

  const createTargetList = (targetListItems, targetListType) => {
    const endpoint = `/api/v0/merchants/${merchantId}/target_lists/${targetListType}/create_target_list/`;

    return api
      .post(endpoint, {
        target_list: {
          target_list_name: `${strategyId}_${targetListType}`,
          target_list_type: targetListType,
          target_list_items: targetListItems,
        },
      })
      .then(resp => resp.data.data[0].target_list_id)
      .catch(err => console.log(err.message));
  };

  const updateTargetList = (targetListItems, targetListType, targetListId) => {
    const endpoint = `/api/v0/merchants/${merchantId}/target_lists/${targetListType}/${targetListId}/update_target_list/`;

    return api
      .put(endpoint, {
        target_list: {
          target_list_items: targetListItems,
        },
      })
      .then(resp => targetListId)
      .catch(err => console.log(err.message));
  };

  const saveTargetExpression = targetRulesData => {
    const createEndpoint = `/api/v0/merchants/${merchantId}/target_expressions/create_target_expression/`;
    const updateEndpoint = `/api/v0/merchants/${merchantId}/target_expressions/${strategy.target_expression_id}/update_target_expression/`;

    const targetRules = targetRulesData.map(
      ({ targetListId, targetRuleId, targetOperator, targetListType }) => {
        const retVal = {
          [getTargetListTypeField(targetListType)]: parseInt(targetListId),
          target_rule_operator: targetOperator,
        };

        if (targetRuleId) {
          retVal.target_rule_id = parseInt(targetRuleId);
        }

        return retVal;
      },
    );

    const payload = {
      target_expression: {
        strategy_id: parseInt(strategyId),
        target_rules: targetRules,
      },
    };

    const apiCall = strategy.target_expression_id
      ? Promise.resolve(api.put(updateEndpoint, payload))
      : Promise.resolve(api.post(createEndpoint, payload));

    return apiCall;
  };

  //  Find more elegant way to not have 2 symmetrical objects
  const getTargetListTypeField = type => {
    const listTypeField = {
      sku: 'target_rule_sku_list_id',
      brand: 'target_rule_brand_list_id',
      google_category: 'target_rule_google_category_list_id',
      merchant_category: 'target_rule_merchant_category_list_id',
      publisher: 'target_rule_publisher_list_id',
    };

    return listTypeField[type];
  };

  const getTargetTypeByField = field => {
    const listFieldType = {
      target_rule_sku_list_id: 'sku',
      target_rule_brand_list_id: 'brand',
      target_rule_google_category_list_id: 'google_category',
      target_rule_merchant_category_list_id: 'merchant_category',
      target_rule_publisher_list_id: 'publisher',
    };

    return listFieldType[field];
  };

  const onAddTargeting = e => {
    e.preventDefault();
    const newValues = {
      ...values,
      [`new-${Object.keys(values).length}`]: {
        target_list_items: [],
        target_list_type: null,
        target_rule_operator: null,
      },
    };

    setValues(newValues);
  };

  const onRemoveTargeting = field => {
    const newValues = { ...values };
    delete newValues[field];
    setValues(newValues);
  };

  const valueEntries = Object.entries(values);
  const selectedTypes = valueEntries.map(([key, value]) => value.target_list_type);

  return (
    <form onSubmit={handleSubmit}>
      <div className="loader">
        <Loader loading={loading} />
      </div>
      <FormSection label="Targeting">
        {!valueEntries.length && !loading && (
          <div className="no-targets">This strategy has no targets</div>
        )}
        {valueEntries.map(([ruleId, value], idx) => {
          return (
            <InputWrapper key={ruleId} label={idx === 0 ? 'Where' : 'And'}>
              <TargetExpressionInput
                name={ruleId}
                value={value}
                onChange={handleChange}
                onRemove={onRemoveTargeting}
                errors={errors[ruleId]}
                pubList={pubList}
                pubMap={pubMap}
                selectedTypes={selectedTypes}
              />
            </InputWrapper>
          );
        })}

        {!loading && <Button label="Add Targeting" kind="secondary" onClick={onAddTargeting} />}
      </FormSection>
      <FormSection>
        <div className="form-buttons">
          <div className="left">
            <Button
              onClick={e => {
                e.preventDefault();
                handleBackButton();
              }}
              label="Back"
              kind="text"
              size="large"
            />
          </div>
          <div className="right">
            <span className="form-error">{formError}</span>
            <Button
              onClick={e => {
                e.preventDefault();
                history.push(
                  `/v2/merchant/${merchantId}/campaign-center/campaigns?strategy_group_ids=${strategyGroupId}`,
                );
              }}
              label="Cancel"
              kind="text"
              size="large"
            />
            <Button submit={true} label="Save and Continue" size="large" />
          </div>
        </div>
      </FormSection>
    </form>
  );
};

export default TargetingPage;
